import { useCurrentStateAndParams, useRouter } from '@uirouter/react';

import {
  UserAccessViewModel,
  TeamAccessViewModel,
} from '@/portainer/models/access';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { AccessDatatable as NamespaceAccessDatatable } from '@/react/portainer/access-control/AccessManagement/AccessDatatable/AccessDatatable';

import { useNamespaceAccesses } from '../useNamespaceAccesses';
import { useUpdateNamespaceAccessesMutation } from '../useUpdateNamespaceAccessesMutation';
import { createAccessesPayload } from '../createAccessesPayload';
import { NamespaceAccess } from '../types';

const tableKey = 'kubernetes_resourcepool_access';

export function AccessDatatable() {
  const {
    params: { id: namespaceName },
  } = useCurrentStateAndParams();
  const router = useRouter();
  const environmentId = useEnvironmentId();

  // Get all namespace access (policy-based and database-based)
  // Similar to useAccesses for environment access
  const {
    authorizedUsersAndTeams,
    policyId,
    policyName,
    hasPolicyApplied,
    isLoading,
  } = useNamespaceAccesses(namespaceName);

  const updateNamespaceAccessMutation = useUpdateNamespaceAccessesMutation(
    environmentId,
    namespaceName
  );

  return (
    <NamespaceAccessDatatable
      tableKey={tableKey}
      dataset={authorizedUsersAndTeams}
      onRemove={handleRemove}
      showWarning={false}
      isUpdateEnabled={!hasPolicyApplied}
      showRoles={false}
      inheritFrom={false}
      policyId={policyId}
      policyName={policyName}
      isUpdatingAccess={updateNamespaceAccessMutation.isLoading}
      isLoading={isLoading}
    />
  );

  async function handleRemove(
    selectedAccesses: Array<UserAccessViewModel | TeamAccessViewModel>
  ) {
    if (hasPolicyApplied) {
      // Should not be called when policy is applied, but handle gracefully
      return;
    }

    try {
      const selectedItemsToRemove: NamespaceAccess[] = selectedAccesses.map(
        (access) => ({
          id: access.Id,
          name: access.Name,
          type: access.Type as 'user' | 'team',
          role: access.Role
            ? { id: access.Role.Id, name: access.Role.Name }
            : undefined,
        })
      );

      const accessesPayload = createAccessesPayload([], selectedItemsToRemove);
      await updateNamespaceAccessMutation.mutateAsync(accessesPayload);
      notifySuccess('Success', 'Namespace access updated');
      router.stateService.reload();
    } catch (error) {
      notifyError('Failed to update namespace access', error as Error);
    }
  }
}
